import { LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { QuotationDTO } from 'components/ordering/quotation/api/dtos/quotation-dto.js'
import { GetQuotationsPayload, ListQuotations } from 'components/ordering/quotation/api/requests/list-quotations.js'
import { executeQuotationRequest } from 'components/ordering/quotation/api/executor.js'
import { createContext } from '@lit/context'
import { provideOnBody } from 'components/shared/context/body-context.js'
import { DeleteQuotation } from 'components/ordering/quotation/api/requests/delete-quotation.js'
import { GetQuotation } from 'components/ordering/quotation/api/requests/get-quotation.js'
import { BadRequestError, InternalServerError, NotFoundError, QuotationError } from 'components/ordering/quotation/api/dtos/error.js'
import { UpdateQuotation, UpdateQuotationPayload } from 'components/ordering/quotation/api/requests/update-quotation.js'
import {
  UpdateQuotationLine,
  UpdateQuotationLinePayload
} from 'components/ordering/quotation/api/requests/update-quotation-line.js'
import { SetDraftQuotation } from 'components/ordering/quotation/api/requests/set-draft-quotation.js'
import { ReopenDraftQuotation } from 'components/ordering/quotation/api/requests/reopen-draft-quotation.js'
import { ReopenQuotation } from 'components/ordering/quotation/api/requests/reopen-quotation.js'
import { isQuotationDTO } from 'components/ordering/quotation/components/is-quotation-dto.js'
import { AddQuotationToBasket } from 'components/ordering/quotation/api/requests/add-quotation-to-basket.js'
import { DeleteQuotationLine } from 'components/ordering/quotation/api/requests/delete-quotation-line.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { WINDOW_LOCATION, WindowLocation } from '@cloudsuite/mosaic-shared/scripts/window-location.js'
import { redirectToPath } from '@cloudsuite/mosaic-shared/scripts/base-url.js'
import { GetOpenQuotation } from 'components/ordering/quotation/api/requests/get-open-quotation.js'
import { SummarizeQuotations } from 'components/ordering/quotation/api/requests/summarize-quotations.js'
import { QuotationSummaryDTO } from 'components/ordering/quotation/api/dtos/quotation-summary-dto.js'
import { CreateQuotationLine } from 'components/ordering/quotation/api/requests/create-quotation-line.js'
import { CreateQuotation } from 'components/ordering/quotation/api/requests/create-quotation.js'

type Currency = {
  id: number
  code: string
  digits: number
  symbol: string
}

export const basketIndexPathContext = createContext<string>(Symbol('BASKET_INDEX_URL_CONTEXT'))
export const quotationConfirmationPagePathContext = createContext<string>(Symbol('QUOTATION_CONFIRMATION_PAGE_URL_CONTEXT'))
export const defaultProductImageContext = createContext<string>(Symbol('DEFAULT_PRODUCT_IMAGE_CONTEXT'))
export const currencyContext = createContext<Currency>(Symbol('CURRENCY_CONTEXT'))
export const localeContext = createContext<Intl.LocalesArgument>(Symbol('LOCALE_CONTEXT'))

export const quotationsDataContext = createContext<QuotationDTO[]>(Symbol('QUOTATIONS_DATA_CONTEXT'))
export const activeQuotationContext = createContext<QuotationDTO | NotFoundError | NotFetchedYet>(Symbol('ACTIVE_QUOTATION_CONTEXT'))
export const openQuotationContext = createContext<QuotationDTO | NotFoundError | NotFetchedYet>(Symbol('OPEN_QUOTATION_CONTEXT'))

export const quotationActionErrorContext = createContext<QuotationError | null>(Symbol('QUOTATION_ACTION_ERROR_CONTEXT'))

export const quotationSummaryContext = createContext<QuotationSummaryDTO | NotFetchedYet>(Symbol('QUOTATION_SUMMARY_CONTEXT'))
export const summarizeQuotationsContext = createContext<() => Promise<void>>(Symbol('SUMMARIZE_QUOTATIONS_CONTEXT'))

export const createQuotationContext = createContext<() => Promise<void>>(Symbol('CREATE_QUOTATION_CONTEXT'))
export const listQuotationsContext = createContext<() => Promise<void>>(Symbol('LIST_QUOTATIONS_CONTEXT'))
export const deleteQuotationContext = createContext<(quotationId: number) => Promise<void>>(Symbol('DELETE_QUOTATION_CONTEXT'))
export const getQuotationByIdContext = createContext<(quotationId: number) => Promise<void>>(Symbol('GET_QUOTATION_BY_ID'))
export const getOpenQuotationContext = createContext<() => Promise<void>>(Symbol('GET_OPEN_QUOTATION_CONTEXT'))
export const updateQuotationInternalWithoutRequestContext = createContext<(payload: QuotationDTO) => Promise<void>>(Symbol('UPDATE_QUOTATION_INTERNAL_WITHOUT_REQUEST_CONTEXT'))
export const updateQuotationContext = createContext<(payload: UpdateQuotationPayload) => Promise<void>>(Symbol('UPDATE_QUOTATION_CONTEXT'))
export const awaitPendingQuotationUpdateContext = createContext<() => Promise<void>>(Symbol('AWAIT_PENDING_QUOTATION_UPDATE_CONTEXT'))
export const updateQuotationLineContext = createContext<(payload: UpdateQuotationLinePayload) => Promise<void>>(Symbol('UPDATE_QUOTATION_LINE_CONTEXT'))
export const setDraftQuotationContext = createContext<(quotationId: number) => Promise<void>>(Symbol('SET_DRAFT_QUOTATION_CONTEXT'))
export const deleteOpenQuotationContext = createContext<() => Promise<void>>(Symbol('DELETE_OPEN_QUOTATION_CONTEXT'))
export const reopenDraftQuotationContext = createContext<(quotationId: number) => Promise<void>>(Symbol('REOPEN_DRAFT_QUOTATION_CONTEXT'))
export const reopenQuotationContext = createContext<(quotationId: number) => Promise<void>>(Symbol('REOPEN_QUOTATION_CONTEXT'))
export const addQuotationToBasketContext = createContext<(quotationId: number) => Promise<void>>(Symbol('ADD_QUOTATION_TO_BASKET_CONTEXT'))

export const deleteQuotationLineContext = createContext<(quotationId: number, lineId: number) => Promise<void>>(Symbol('DELETE_QUOTATION_LINE_CONTEXT'))
export const createQuotationLineContext = createContext<(quotationId: number, productId: number) => Promise<void>>(Symbol('CREATE_QUOTATION_LINE_CONTEXT'))

export class NotFetchedYet {
  notFetchedYetBrand = Symbol('NOT_FETCHED_YET_BRAND')
}

const toQuotationError = (error: unknown): QuotationError => {
  if (error instanceof QuotationError) return error

  const message = error instanceof Error ? error.message : String(error)
  return new InternalServerError('Unexpected error', message)
}

const documentLocale = (): string => document.documentElement.lang || 'en-US'

const parseLocale = (locale: string | null): Intl.LocalesArgument => {
  if (!locale) return documentLocale()

  const [language, region, ...rest] = locale.split('_')
  if (region === undefined || rest.length > 0) return locale

  console.warn(`<csc-quotation-context> locale "${locale}" is deprecated, use the BCP 47 format with a hyphen, e.g. "nl-NL".`)
  return `${language?.toLowerCase()}-${region.toUpperCase()}`
}


@customElement('csc-quotation-context')
export class CSCQuotationContext extends LitElement {
  protected override createRenderRoot = () => this

  @inject(WINDOW_LOCATION)
  private windowLocation!: WindowLocation

  @provideOnBody({ context: basketIndexPathContext })
  @property({ attribute: 'basket-index-path', type: String })
  public basketIndexPath!: string

  @provideOnBody({ context: quotationConfirmationPagePathContext })
  @property({ attribute: 'quotation-confirmation-page-path', type: String })
  public quotationConfirmationPagePath!: string

  @provideOnBody({ context: defaultProductImageContext })
  @property({ attribute: 'default-product-image', type: String })
  public defaultProductImage!: string

  @provideOnBody({ context: currencyContext })
  @property({ attribute: 'currency', type: Object })
  public currency!: Currency

  @provideOnBody({ context: localeContext })
  @property({ attribute: 'locale', type: String, converter: parseLocale })
  public locale: Intl.LocalesArgument = documentLocale()

  @provideOnBody({ context: quotationsDataContext })
  public quotationsData: QuotationDTO[] = []

  @provideOnBody({ context: activeQuotationContext })
  public activeQuotation: QuotationDTO | NotFoundError | NotFetchedYet = new NotFetchedYet()

  @provideOnBody({ context: openQuotationContext })
  public openQuotation: QuotationDTO | NotFoundError | NotFetchedYet = new NotFetchedYet()

  @provideOnBody({ context: quotationSummaryContext })
  public quotationSummary: QuotationSummaryDTO | NotFetchedYet = new NotFetchedYet()

  private requestCache: Map<string, Promise<void>> = new Map()

  @provideOnBody({ context: summarizeQuotationsContext })
  public summarizeQuotations = async () => {
    return this.deduplicateRequest('summarizeQuotations', async () => {
      const request = new SummarizeQuotations()
      this.quotationSummary = await executeQuotationRequest(request)
    })
  }

  @provideOnBody({ context: listQuotationsContext })
  public listQuotations = async (filters: GetQuotationsPayload = {}) => {
    const cacheKey = `listQuotations:${JSON.stringify(filters)}`
    return this.deduplicateRequest(cacheKey, async () => {
      const request = new ListQuotations(filters)
      this.quotationsData = await executeQuotationRequest(request)
    })
  }

  @provideOnBody({ context: deleteQuotationContext })
  public deleteQuotation = async (quotationId: number) => {
    return this.executeAction(async () => {
      const deleteRequest = new DeleteQuotation({ quotationId: quotationId })
      await executeQuotationRequest(deleteRequest)

      await this.listQuotations()
    })
  }

  @provideOnBody({ context: quotationActionErrorContext })
  public actionError: QuotationError | null = null

  private async executeAction(action: () => Promise<void>): Promise<void> {
    this.actionError = null

    try {
      await action()
    } catch (error) {
      this.actionError = toQuotationError(error)
    }
  }

  private setActiveQuotation(response: QuotationDTO | BadRequestError | NotFoundError) {
    if (response instanceof QuotationDTO) {
      this.activeQuotation = response
    } else {
      throw response
    }
  }

  private setOpenQuotation(response: QuotationDTO | BadRequestError | NotFoundError) {
    if (response instanceof QuotationDTO) {
      this.openQuotation = response
    } else {
      throw response
    }
  }

  private async deduplicateRequest<T>(
    cacheKey: string,
    requestFn: () => Promise<T>
  ): Promise<T> {
    // Return cached promise if already in-flight
    if (this.requestCache.has(cacheKey)) {
      return this.requestCache.get(cacheKey) as Promise<T>
    }

    // Create and cache the new request
    const promise = (async () => {
      try {
        return await requestFn()
      } finally {
        // Clear cache when done
        this.requestCache.delete(cacheKey)
      }
    })()

    this.requestCache.set(cacheKey, promise as Promise<void>)
    return promise
  }

  @provideOnBody({ context: createQuotationContext })
  public createQuotation = async () => {
    return this.executeAction(async () => {
      await this.getOpenQuotation()
      if (this.openQuotation instanceof QuotationDTO) {
        // Only set activeQuotation if not already explicitly set by detail page
        if (!(this.activeQuotation instanceof QuotationDTO)) {
          this.setActiveQuotation(this.openQuotation)
        }
        return
      }

      const request = new CreateQuotation()
      const response = await executeQuotationRequest(request)
      this.setActiveQuotation(response)
    })
  }

  @provideOnBody({ context: getQuotationByIdContext })
  public getQuotationById = async (quotationId: number) => {
    return this.deduplicateRequest(`getQuotationById:${quotationId}`, async () => {
      const request = new GetQuotation({ quotationId: quotationId })
      const response = await executeQuotationRequest(request)
      this.setActiveQuotation(response)
    })
  }

  @provideOnBody({ context: getOpenQuotationContext })
  public getOpenQuotation = async () => {
    return this.deduplicateRequest('getOpenQuotation', async () => {
      const request = new GetOpenQuotation()
      const response = await executeQuotationRequest(request)
      if (response instanceof QuotationDTO) {
        this.setOpenQuotation(response)
        // Also set as active if no specific quotation is already active
        if (!isQuotationDTO(this.activeQuotation)) {
          this.setActiveQuotation(response)
        }
      }
    })
  }

  @provideOnBody({ context: updateQuotationInternalWithoutRequestContext })
  public updateQuotationInternalWithoutRequest = async (quotation: QuotationDTO) => {
    if (!isQuotationDTO(quotation)) {
      throw new Error('Invalid quotation provided')
    }
    this.setOpenQuotation(quotation)
    this.setActiveQuotation(quotation)
  }

  private pendingQuotationUpdate: Promise<void> = Promise.resolve()

  @provideOnBody({ context: updateQuotationContext })
  public updateQuotation = async (payload: UpdateQuotationPayload) => {
    const request = new UpdateQuotation(payload)
    this.pendingQuotationUpdate = this.executeAction(async () => {
      const response = await executeQuotationRequest(request)
      this.setActiveQuotation(response)
    })
    await this.pendingQuotationUpdate
  }

  @provideOnBody({ context: awaitPendingQuotationUpdateContext })
  public awaitPendingQuotationUpdate = async () => {
    await this.pendingQuotationUpdate
  }

  @provideOnBody({ context: updateQuotationLineContext })
  public UpdateQuotationLine = async (payload: UpdateQuotationLinePayload) => {
    return this.executeAction(async () => {
      const request = new UpdateQuotationLine(payload)
      const response = await executeQuotationRequest(request)
      this.setActiveQuotation(response)
    })
  }

  @provideOnBody({ context: setDraftQuotationContext })
  public setDraftQuotation = async (quotationId: number) => {
    return this.executeAction(async () => {
      const request = new SetDraftQuotation({ quotationId: quotationId })
      const response = await executeQuotationRequest(request)
      this.setActiveQuotation(response)
    })
  }

  @provideOnBody({ context: deleteOpenQuotationContext })
  public deleteOpenQuotation = async () => {
    return this.executeAction(async () => {
      const request = new GetOpenQuotation()
      const response = await executeQuotationRequest(request)

      if (!(response && isQuotationDTO(response))) {
        return
      }

      const deleteRequest = new DeleteQuotation({ quotationId: response.id })
      await executeQuotationRequest(deleteRequest)

      await this.listQuotations()
    })
  }

  @provideOnBody({ context: reopenDraftQuotationContext })
  public reopenDraftQuotation = async (quotationId: number) => {
    return this.executeAction(async () => {
      const request = new ReopenDraftQuotation({ quotationId: quotationId })
      const response = await executeQuotationRequest(request)
      this.setActiveQuotation(response)
    })
  }

  @provideOnBody({ context: reopenQuotationContext })
  public reopenQuotation = async (quotationId: number) => {
    return this.executeAction(async () => {
      const request = new ReopenQuotation({ quotationId: quotationId })
      const response = await executeQuotationRequest(request)

      if (!isQuotationDTO(response)) {
        throw response
      }

      await redirectToPath(this.windowLocation, `account/quotation/${response.id}/`)
    })
  }

  @provideOnBody({ context: addQuotationToBasketContext })
  public addQuotationToBasket = async (quotationId: number) => {
    return this.executeAction(async () => {
      const request = new AddQuotationToBasket({ quotationId: quotationId })
      const response = await executeQuotationRequest(request)

      if (!isQuotationDTO(response)) {
        throw response
      }

      await redirectToPath(this.windowLocation, `account/quotation/${response.id}/`)
    })
  }

  @provideOnBody({ context: deleteQuotationLineContext })
  public deleteQuotationLine = async (quotationId: number, lineId: number) => {
    return this.executeAction(async () => {
      const request = new DeleteQuotationLine({ quotationId: quotationId, lineId: lineId })
      const response = await executeQuotationRequest(request)
      this.setActiveQuotation(response)
    })
  }

  @provideOnBody({ context: createQuotationLineContext })
  public createQuotationLine = async (quotationId: number, productId: number) => {
    return this.executeAction(async () => {
      const request = new CreateQuotationLine({ quotationId: quotationId, productId: productId })
      const response = await executeQuotationRequest(request)
      this.setActiveQuotation(response)
    })
  }
}
